import React from "react"

import { Input } from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { CHAT_WEBSOCKET_TOOLTIPS } from "./tooltips"
import type { ChatWebSocketFieldProps } from "./types"

export const TextMessageFormatFields: React.FC<ChatWebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  const textMessageTypeValues = Array.isArray(metadata.text_message_type_values)
    ? metadata.text_message_type_values
    : typeof metadata.text_message_type_values === "string"
      ? metadata.text_message_type_values
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : []

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">
        Text Message Format
      </h4>

      {/* Send Message Template */}
      <div className="space-y-2">
        <FieldLabel tooltip={CHAT_WEBSOCKET_TOOLTIPS.sendMessageTemplate}>
          Send Message Template
        </FieldLabel>
        <JsonCodeEditor
          ariaLabel="Send Message Template"
          placeholder={'{\n  "type": "message",\n  "text": "{{message}}"\n}'}
          value={metadata.send_message_template || ""}
          onChange={(value) =>
            onMetadataUpdate({ send_message_template: value })
          }
          rows={4}
          disabled={disabled}
          // `{{message}}` may stand in for a whole value, which a plain parse
          // rejects.
          lint={false}
        />
        <p className="text-xs text-coval-text-muted">
          JSON template for outgoing text messages. Use{" "}
          <code className="text-xs bg-surface-1 px-1 rounded">
            {"{{message}}"}
          </code>{" "}
          as a placeholder for the message text.
        </p>
      </div>

      {/* Receive Message Text Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="receive-text-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageTextPath}
        >
          Receive Message Text Path
        </FieldLabel>
        <Input
          id="receive-text-path"
          aria-label="Receive Message Text Path"
          placeholder="text"
          value={metadata.receive_message_text_path || ""}
          onChange={(e) =>
            onMetadataUpdate({ receive_message_text_path: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Dot-notation path to extract text content from received messages
          (e.g., &quot;body.text&quot;).
        </p>
      </div>

      {/* Receive Message Type Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="receive-type-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageTypePath}
        >
          Receive Message Type Path
        </FieldLabel>
        <Input
          id="receive-type-path"
          aria-label="Receive Message Type Path"
          placeholder="type"
          value={metadata.receive_message_type_path || ""}
          onChange={(e) =>
            onMetadataUpdate({ receive_message_type_path: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Dot-notation path to extract the message type from received messages
          (e.g., &quot;class&quot;).
        </p>
      </div>

      {/* Text Message Type Values */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="text-message-types"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.textMessageTypeValues}
        >
          Text Message Type Values
        </FieldLabel>
        <Input
          id="text-message-types"
          aria-label="Text Message Type Values"
          placeholder="message"
          value={textMessageTypeValues.join(", ")}
          onChange={(e) =>
            onMetadataUpdate({
              text_message_type_values: e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
            })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Comma-separated list of message type values that indicate text content
          (e.g., &quot;StructuredMessage, TextMessage&quot;).
        </p>
      </div>

      {/* Agent Disconnect Message Type */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="disconnect-type"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.agentDisconnectMessageType}
        >
          Agent Disconnect Message Type
        </FieldLabel>
        <Input
          id="disconnect-type"
          aria-label="Agent Disconnect Message Type"
          placeholder="SessionDisconnected"
          value={metadata.agent_disconnect_message_type || ""}
          onChange={(e) =>
            onMetadataUpdate({ agent_disconnect_message_type: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Message type that signals the agent has disconnected. Leave empty to
          disable.
        </p>
      </div>

      {/* Direction Filter Fields */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="direction-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageDirectionPath}
        >
          Receive Message Direction Path
        </FieldLabel>
        <Input
          id="direction-path"
          aria-label="Receive Message Direction Path"
          placeholder="body.direction"
          value={metadata.receive_message_direction_path || ""}
          onChange={(e) =>
            onMetadataUpdate({
              receive_message_direction_path: e.target.value,
            })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Dot-notation path to extract direction from received messages. Used to
          filter echo messages. Leave empty to process all messages.
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel
          htmlFor="outbound-direction"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.outboundDirectionValue}
        >
          Outbound Direction Value
        </FieldLabel>
        <Input
          id="outbound-direction"
          aria-label="Outbound Direction Value"
          placeholder="Outbound"
          value={metadata.outbound_direction_value || ""}
          onChange={(e) =>
            onMetadataUpdate({ outbound_direction_value: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Direction value indicating an agent (outbound) message. Only used when
          direction path is set.
        </p>
      </div>
    </div>
  )
}
